import {
  trackEvent as trackEventUtil,
  trackNonInteractionEvent,
} from "@leafly-com/web-utils";
import startCase from "lodash/startCase";

import { CartItem } from "custom-types/Cart";

/*
 * Please try to keep GA event data clean. It is getting out of hand. See:
 * https://docs.google.com/document/d/1FNtKK4QUkgHUulxzJQC0cxI5OyO6xVwBbB9mdZgtJZg/edit?usp=sharing
 */

export enum Category {
  addToBag = "add to bag",
  bag = "bag",
  brandResults = "brand results",
  button = "button",
  Carousel = "Carousel",
  carousel = "carousel",
  checkout = "checkout",
  checkoutConfirmation = "checkout confirmation page",
  checkoutPage = "checkout page",
  confirmInfo = "ConfirmInfo",
  dealCards = "deal cards",
  dealMenuPhenotypes = "deal_menu_phenotypes",
  dealMenuProductCategories = "deal_menu_product_categories",
  dealMenuStrains = "deal_menu_strains",
  dealsBogoCard = "deal cards - bogo",
  dealsCarouselCard = "deals_carousel_card",
  dealsMenuBrowseDeals = "deals menu browse deals",
  dealsMenuBrowsePromotions = "deals menu browse promotions",
  dealsMenuDealsCard = "Deals Menu Deals Card",
  dealsMenuLoadMoreDeals = "deals_menu_load_more_deals_button",
  dealsMenuPromotionsCard = "Deals Menu Promotions Card",
  dealsMenuSeeAllLocations = "see all locations link",
  dealsMenuSponsoredDealsCard = "deals menu sponsored deals card",
  dealsMerchandising = "deals_merchandising",
  dealsOnTopStrains = "deals on top strains",
  deliveryAddressModal = "Delivery Address Modal",
  deliveryGateFlow = "Delivery Gate Flow",
  descriptionLinks = "strain description links",
  dispensaryCard = "dispensary card",
  dispensaryFavorite = "dispensary favorite",
  dispensaryFooter = "dispensary footer",
  dispensaryHeader = "dispensary header",
  dispensaryHours = "dispensary hours",
  dispensaryInfo = "Dispensary Info",
  dispensaryMenu = "dispensary menu",
  dispensaryMenuPdp = "dispensary menu pdp",
  dispensaryMenuFilter = "Dispensary Menu Filter",
  dispensaryMenuProductTypeShortcut = "Dispensary Menu Product Type Shortcut",
  dispensaryMenuSort = "Dispensary Menu Sort",
  dispensaryShop = "Shop dispensary",
  doctorProfile = "doctor profile",
  emptyBagState = "Empty Bag State",
  favorite = "favorite",
  featuredDealCards = "deal cards - featured",
  feedbackSurvey = "feedback survey",
  finder = "finder",
  finderFilter = "finder filter",
  finderSort = "finder sort",
  header = "header",
  headerNavigation = "header navigation",
  headerSearch = "header search",
  homepageFeature = "Homepage Feature",
  homepageSearch = "homepage_search",
  loadMoreBrandResults = "load more brand results button",
  loadMoreDealsButton = "load more deals button",
  loadMorePromotionsButton = "load more promotions button",
  loadMoreResults = "load more results button",
  map = "map",
  mapSpotlight = "map spotlight",
  dispensaryStrainDetails = "dispensary strain details",
  menuDetails = "Dispensary Menu Details View",
  menuItemCard = "menu item card",
  menuItemCardTray = "menu item card tray",
  menuItemDetailVariants = "menu item detail variants",
  newsArticleCard = "news article card",
  notification = "Notification",
  orderAgainCarousel = "Order Again Carousel",
  orderHistory = "Order History",
  productCategoryCoinFilter = "product category coin filter",
  productFilterBar = "product_filter_bar",
  productPage = "Products",
  recentlyViewedMenuItems = "recently viewed menu items",
  resultsSorted = "results sorted",
  retailerCard = "retailer card",
  reviews = "Reviews",
  reservationStatus = "Reservation Status",
  schedulingModal = "scheduling modal",
  searchResults = "search_results",
  searchShopResults = "search_shop_results",
  shopAllMenus = "shop all menus button",
  strainsFilter = "Strains Filter",
  strainsSort = "Strains Sort",
  strainType = "strain type",
  showAllLocationResults = "show all location results button",
  showMoreNewsResults = "show more news results button",
  showMoreStrainResultsButton = "show_more_strain_results_button",
  sitewideBanner = "sitewide banner",
  strainCard = "strain_card",
  strainDescriptionLink = "strain description links",
  strainReview = "Strain Review Page",
  strainTypeFilter = "strain type filter",
  viewDispensaryMap = "view dispensary map button",
  webProfile = "Web Profile",
  writeStrainReview = "Write Strain Review",
  homepagePaidAd = "Paid Homepage Marquee",
  homepageHouseAd = "House Homepage Marquee",
  strainListPaidAd = "Paid Strain List Marquee",
  pagination = "pagination",
  strainListHouseAd = "House Strain List Marquee",
  dealsPageListPaidAd = "Paid Deals Page Marquee",
  dealsPageListHouseAd = "House Deals Page Marquee",
}

export enum Action {
  affiliateLinkClick = "BUY HERE",
  categorySelected = "category_selected",
  click = "click",
  Click = "Click",
  contactClicked = "Contact clicked",
  dealsMerchandisingClicked = "deals_merchandising_clicked",
  dealsMerchandisingImpression = "deals_merchandising_impression",
  directionsClicked = "Directions clicked",
  emailAdded = "Email added",
  impression = "impression",
  nameAdded = "Name added",
  notificationClick = "notification click",
  phoneAdded = "Phone added",
  searchResultClicked = "search_result_clicked",
  searchResultsFiltered = "search_results_filtered",
  searchResultsSorted = "search_shop_results_sorted",
  searchShopNullResults = "search_shop_null_results",
  snackbar = "snackbar",
  submit = "Submit",
}

export enum Label {
  strainNameLink = "strain name link",
  page = "page",
  nextArrow = "next arrow",
  previousArrow = "previous arrow",
  learnMoreAboutStrainLink = "learn more about strain link",
  addToBag = "add to bag",
  strainReview = "strain reviews",
  changeDateAndTime = "change date and time",
  viewStrainDetailsModule = "view strain details module",
  confirmation = "confirmation",
  confirmOrder = "confirm order",
  closeSchedule = "close schedule",
  dispensaryReservations = "Dispensary Reservations",
  menuItemCardViewDetails = "menu item card - view details",
  menuItemCardViewDetailsButton = "menu item card - view details button",
  favoriteDispensaryStrainDetails = "favorite dispensary strain details",
  unfavoriteDispensaryStrainDetails = "unfavorite dispensary strain details",
  nullResults = "null_results",
  selectedOption = "selected_option=",
  scheduleEnabled = "schedule enabled",
  modal = "modal",
  showMoreStrainResultsButton = "show more strain results button",
  submitAppointment = "Submit Appointment",
  variantConfirmation = "variant confirmation",
  variantSelection = "variant selection",
  viewBag = "view bag",
  writeReview = "write review",
  appDownloadBanner = "app download banner",
  appDownloadOpen = "app download banner - open",
  appDownloadClose = "app download banner - close",
  requestOnlineOrdering = "request online ordering",
}

export type Dimension = {
  [name: string]: number | string | boolean | undefined | null;
};

export const trackEvent = (
  category: Category,
  action: Action,
  label?: string,
  customDimensions?: Dimension
) => trackEventUtil(category, action, label, customDimensions);

export const trackMenuEvent = (
  field: string,
  label: string,
  {
    menuItemId,
    menuItem,
    category,
    cartId,
    janeCheckoutEnabled,
  }: {
    menuItemId?: string | number | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
    retailType?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
    menuItem?: any;
    category?: string | null;
    cartId?: string | null;
    janeCheckoutEnabled?: boolean | null;
  } = {}
) => {
  const renamedFields = {
    Categories: "ProductType",
    Varieties: "Classification",
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  } as any;

  let action = startCase(field).replace(/[^A-Za-z0-9]/g, "");

  if (renamedFields[action]) {
    action = renamedFields[action];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  const itemDimensions = {} as any;

  if (menuItem) {
    const featuredDeal = menuItem?.deal || menuItem?.offers?.[0];
    itemDimensions.menuItemId = menuItem.id || menuItem.menuItemId;
    itemDimensions.brandId = menuItem.brandId || menuItem.brand?.id;
    itemDimensions.productId = menuItem.productId || menuItem.product?.id;
    itemDimensions.strainId = menuItem.strainId || menuItem.strain?.id;
    itemDimensions.dispenaryId = menuItem.dispensaryId;
    itemDimensions.dealId = featuredDeal?.id || "no deal";
    itemDimensions.dealType = featuredDeal?.kind || "no deal";
    itemDimensions.dealDiscountType = featuredDeal?.discountType || "no deal";
  } else if (menuItemId) {
    itemDimensions.menuItemId = menuItemId;
  }

  if (cartId) {
    itemDimensions.cartId = cartId;
    itemDimensions.janeCheckoutEnabled = !!janeCheckoutEnabled;
  }
  trackEventUtil(category || "Dispensary Menu", action, label, itemDimensions);
};

export const trackFilterEventForClients = (
  // @ts-ignore (fix me please, do not replicate)
  label,
  // @ts-ignore (fix me please, do not replicate)
  value,
  customDimensions = null
) => {
  const clientTrackingLabel = {
    brand_name: "Brands",
    cbd_pips: "Cbd",
    pickup_available: "PickupFacet",
    price: "Price",
    product_category: "ProductType",
    strain_category: "Cultivars",
    strain_name: "Strains",
    thc_pips: "Thc",
    unit: "Unit",
  };

  const clientTrackingValue = {
    "cbd_content:asc": "CBD: Low",
    "cbd_content:desc": "CBD: High",
    "created:asc": "Oldest",
    "created:desc": "Newest",
    delivery_available: "Delivery",
    each: "Each",
    g: "Gram",
    "menu_item_name:asc": "A-Z",
    mg: "Milligram",
    oz: "Ounce",
    pc: "Piece",
    pickup_available: "Pickup",
    pickup_delivery_available: "Pickup & Delivery",
    "price:asc": "Price: Low",
    "price:desc": "Price: High",
    "thc_content:asc": "THC: Low",
    "thc_content:desc": "THC: High",
    "upvotes_count:asc": "Least Helpful",
    "upvotes_count:desc": "Most Helpful",
  };

  if (label === "pickup_available") {
    value = value === "Select All" ? "Off" : "On";
  }

  trackEventUtil(
    "Dispensary Menu",
    // @ts-ignore (fix me please, do not replicate)
    clientTrackingLabel[label] || label,
    // @ts-ignore (fix me please, do not replicate)
    clientTrackingValue[value] || value,
    customDimensions
  );
};

export const trackInfoEvent = (
  field: string,
  label?: string,
  retailType?: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  customDimension: any = null
): void => {
  //TODO: Add GA Tracker for appointment page.
  const action = startCase(field).replace(/[^A-Za-z0-9]/g, "");
  if (retailType === "clinic") {
    trackEventUtil("Doctor Info", action, label, customDimension);
  } else {
    trackEventUtil("Dispensary Info", action, label, customDimension);
  }
};

export const trackReviewsEvent = (field: string, label?: string) => {
  const action = startCase(field).replace(/[^A-Za-z0-9]/g, "");
  trackEventUtil("Dispensary Reviews", action, label);
};

export const trackCheckoutEvent = (
  field: string,
  label: string,
  {
    bagId,
    category,
    cartItem,
    errorMessage,
    orderId,
    userId,
    subtotal,
    janeCheckoutEnabled,
    bogoDealCount,
    saleDealCount,
  }: {
    bagId?: string | number | null;
    category?: string | null;
    cartItem?: CartItem | null;
    errorMessage?: string | null;
    orderId?: string | null;
    userId?: string | number | null;
    subtotal?: number;
    janeCheckoutEnabled?: boolean | null;
    bogoDealCount?: number | null;
    saleDealCount?: number | null;
  },
  isNonInteractionEvent = false
) => {
  const action = startCase(field).replace(/[^A-Za-z0-9]/g, "");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  let customDimensions = {} as any;

  if (cartItem && cartItem.menuItemData) {
    const {
      brandId,
      dispensaryId,
      id: menuItemId,
      productId,
      strainId,
    } = cartItem.menuItemData;

    customDimensions = {
      brandId,
      dispensaryId,
      janeCheckoutEnabled,
      menuItemId,
      productId,
      strainId,
    };
  }

  if (bagId) {
    customDimensions.bagId = bagId;
  }

  if (orderId) {
    customDimensions.orderId = orderId;
  }

  if (errorMessage) {
    customDimensions.errorMessage = errorMessage;
  }

  if (userId !== undefined) {
    // if userId is null that means guest checkout... seems significant
    customDimensions.userId = userId;
  }

  if (subtotal) {
    customDimensions.subtotal = subtotal;
  }

  if (bogoDealCount !== undefined || saleDealCount !== undefined) {
    customDimensions.bogoDealCount = bogoDealCount || 0;
    customDimensions.saleDealCount = saleDealCount || 0;
  }

  if (isNonInteractionEvent) {
    trackNonInteractionEvent(
      category || "Dispensary Reservations",
      action,
      label,
      customDimensions
    );
  } else {
    trackEventUtil(
      category || "Dispensary Reservations",
      action,
      label,
      customDimensions
    );
  }
};
